import { Fragment, PropsWithChildren, useContext } from "react";
import { Box } from "@mui/material";
import flex from "@/styles/flex";
import { EditableGroupContext } from "../../EditableGroup/EditableGroup";
import styles from "../styles";
import { EditableFieldProps } from "../EditableField";
import { useReleaseManagement, ReleaseFlag } from "@/utility/feature-management";
import { useTranslation } from "react-i18next";
import CopyIconButton from "@/components/common/CopyIconButton";

interface ViewableProps {
    children?: React.ReactNode,
    allowOverflow?: boolean,
    style?: React.CSSProperties,
    copyable?: boolean,
    fieldLabel?: string
}

const Viewable = (props: PropsWithChildren<ViewableProps>) => {
    const state = useContext(EditableGroupContext);
    const fieldProps = useContext(EditableFieldProps);
    const { t } = useTranslation();
    let children = props.children;

    const notesForDriversFlag = useReleaseManagement(ReleaseFlag.releaseNotesForDrivers);
    const isCopyFeatureFlagReleased = useReleaseManagement(ReleaseFlag.releaseCopyEmailToClipboard);

    const textStyling = notesForDriversFlag ? styles.inheritText : styles.text;

    const originalValue = (typeof props.children === "string" ? props.children : props.children?.toString()) || "";
    const shouldShowCopy = isCopyFeatureFlagReleased && props.copyable;

    const generateTooltip = () => {
        if (props.fieldLabel) {
            return t("general.copyFieldToClipboard", { field: props.fieldLabel.toLowerCase() });
        }
        return t("general.copyToClipboard");
    };

    if (children == null || children?.toString().length == 0) {
        children = <Fragment>-</Fragment>;
    }

    const renderContent = (content: React.ReactNode) => {
        if (!shouldShowCopy) {
            return content;
        }

        return (
            <Box sx={{
                display: "flex",
                alignItems: "center",
                width: "100%",
                minWidth: 0
            }}>
                <Box
                    title={originalValue}
                    sx={{
                        fleShrink: 1,
                        minWidth: 0,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        marginRight: 1
                    }}>
                    {content}
                </Box>
                <Box sx={{ flexShrink: 0 }}>
                    <CopyIconButton
                        textToCopy={originalValue}
                        tooltip={generateTooltip()}
                        copiedTooltip={t("general.copied")}
                        testId={`copy-${props.fieldLabel?.toLowerCase().replace(/\s+/g, '-') || 'field'}-button`}
                    />
                </Box>
            </Box>
        );
    };

    if (state.isEditing != true && fieldProps.readOnly != true) {
        return <div style={{ ...flex.flexFill, ...textStyling, ...(props.style ?? {}) }}>
            <div title={originalValue} style={props.allowOverflow == true ? styles.paddingViewableWithOverflow : styles.paddingViewable}>
                {renderContent(children)}
            </div>
        </div>
    }
    if (fieldProps.readOnly == true && (state.isEditing != true || fieldProps.showValueWhenReadOnly == true)) {
        return <div title={originalValue} style={{ ...(props.allowOverflow == true ? styles.paddingViewableWithOverflow : styles.paddingViewable), ...styles.text, ...(props.style ?? {}) }}>
            {renderContent(children)}
        </div>;
    }
    return <Fragment />
}

export default Viewable;